import { Alert, Box, Button, Grid, IconButton, Typography } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import ContentCopy from "@mui/icons-material/ContentCopy";
import { useHierarchyContext } from "../contexts/HierarchyContext";
import { useTimeRangeFromQuery } from "../hooks/useRouteParams";
import {
  listIngressTokensForService,
  type ServiceIngressToken,
  revokeIngressTokenForService,
} from "../api/client";
import { useMetadata } from "../hooks/useMetadata";
import { FilterBar } from "../components/filters/FilterBar";
import { TimeRangeFilterBar } from "../components/filters/TimeRangeFilterBar";
import { Panel } from "../components/layout/Panel";
import { CompGrid, type Column } from "../components/grid/CompGrid";

async function fetchIngressUrl(): Promise<string> {
  const res = await fetch("/api/ingress-url");
  if (!res.ok) throw new Error("Failed to fetch ingress URL");
  const data = (await res.json()) as { ingress_url: string };
  return data.ingress_url;
}

interface InstanceRow {
  serviceName: string;
  instanceId: string;
  firstSeen: Date;
  lastSeen: Date;
}

export function PageIngressTokens() {
  const { hierarchy } = useHierarchyContext();
  const serviceSlug = hierarchy?.service?.slug ?? null;
  const { range } = useTimeRangeFromQuery();
  const serviceIngressTokenNewHref = serviceSlug
    ? `/services/${encodeURIComponent(serviceSlug)}/ingress-tokens/new`
    : null;
  const { metadata, loading: metadataLoading, error: metadataError } = useMetadata(serviceSlug, {
    timeRange: range,
  });

  const [ingressTokens, setIngressTokens] = useState<ServiceIngressToken[]>([]);
  const [tokensLoading, setTokensLoading] = useState(false);
  const [tokensError, setTokensError] = useState<string | null>(null);
  const [ingressUrl, setIngressUrl] = useState<string | null>(null);
  const [ingressUrlError, setIngressUrlError] = useState<string | null>(null);

  useEffect(() => {
    fetchIngressUrl()
      .then(setIngressUrl)
      .catch((err) => setIngressUrlError(err instanceof Error ? err.message : "Failed to load"));
  }, []);

  const instanceRows = useMemo<InstanceRow[]>(
    () => {
      if (!metadata) return [];

      return metadata.services.flatMap((svc) =>
        svc.instances.map((instance) => ({
          serviceName: svc.service_name,
          instanceId: instance.instance_id,
          firstSeen: new Date(instance.first_seen),
          lastSeen: new Date(instance.last_seen),
        })),
      );
    },
    [metadata],
  );

  const instanceColumns: Column<InstanceRow>[] = [
    {
      id: "instanceId",
      label: "Instance",
      sortable: true,
      sortValue: (r) => r.instanceId,
      render: (r) => (
        <Typography variant="body2" className="om-text-mono">
          {r.instanceId}
        </Typography>
      ),
    },
    {
      id: "serviceName",
      label: "Service",
      sortable: true,
      sortValue: (r) => r.serviceName,
      render: (r) => <Typography variant="body2">{r.serviceName}</Typography>,
    },
    {
      id: "firstSeen",
      label: "First Seen",
      sortable: true,
      sortValue: (r) => r.firstSeen,
      render: (r) => <Typography variant="body2">{r.firstSeen.toLocaleString()}</Typography>,
    },
    {
      id: "lastSeen",
      label: "Last Seen",
      sortable: true,
      sortValue: (r) => r.lastSeen,
      render: (r) => <Typography variant="body2">{r.lastSeen.toLocaleString()}</Typography>,
    },
  ];

  const tokenColumns: Column<ServiceIngressToken>[] = [
    {
      id: "name",
      label: "Name",
      sortable: true,
      sortValue: (t) => t.name,
      render: (t) => <Typography variant="body2">{t.name}</Typography>,
    },
    {
      id: "token",
      label: "Token",
      sortable: true,
      sortValue: (t) => t.token,
      render: (t) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Typography variant="body2" className="om-text-mono">
            {t.token}
          </Typography>
          <IconButton
            size="small"
            onClick={async () => {
              try {
                await navigator.clipboard.writeText(t.token);
              } catch {
                // ignore
              }
            }}
            sx={{ opacity: 0.7, "&:hover": { opacity: 1 } }}
            aria-label="Copy token"
          >
            <ContentCopy fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
    {
      id: "created_at",
      label: "Created",
      sortable: true,
      sortValue: (t) => new Date(t.created_at),
      render: (t) => (
        <Typography variant="body2" sx={{ opacity: 0.75 }}>
          {new Date(t.created_at).toLocaleString()}
        </Typography>
      ),
    },
    {
      id: "status",
      label: "Status",
      sortable: true,
      sortValue: (t) => (t.revoked_at ? 1 : 0),
      render: (t) => (
        <Typography variant="body2" sx={{ opacity: 0.75 }}>
          {t.revoked_at ? "revoked" : "active"}
        </Typography>
      ),
    },
    {
      id: "actions",
      label: serviceIngressTokenNewHref ? (
        <Box sx={{ display: "flex", justifyContent: "flex-end", mx: 4 }}>
          <Button
            size="small"
            variant="text"
            component={RouterLink}
            to={serviceIngressTokenNewHref}
            disabled={tokensLoading}
          >
            Create
          </Button>
        </Box>
      ) : null,
      render: (t) => (
        <Box sx={{ display: "flex", justifyContent: "flex-end", mx: 4 }}>
          {!t.revoked_at ? (
            <Button
              size="small"
              variant="text"
              onClick={() => handleRevokeToken(t.id)}
              disabled={tokensLoading}
            >
              Revoke
            </Button>
          ) : (
            <Typography variant="body2" sx={{ opacity: 0.5 }}>
              -
            </Typography>
          )}
        </Box>
      ),
    },
  ];

  useEffect(() => {
    if (!serviceSlug) return;
    const abort = new AbortController();
    setTokensLoading(true);
    setTokensError(null);
    listIngressTokensForService(serviceSlug, abort.signal)
      .then((res) => {
        if (abort.signal.aborted) return;
        setIngressTokens(res);
      })
      .catch((err) => {
        if (abort.signal.aborted) return;
        setTokensError(err instanceof Error ? err.message : "Failed to load ingress tokens");
      })
      .finally(() => {
        if (abort.signal.aborted) return;
        setTokensLoading(false);
      });
    return () => abort.abort();
  }, [serviceSlug]);

  const handleRevokeToken = async (tokenId: string) => {
    if (!serviceSlug) return;
    try {
      setTokensLoading(true);
      setTokensError(null);
      await revokeIngressTokenForService(serviceSlug, tokenId, undefined);
      setIngressTokens((prev) =>
        prev.map((t) => (t.id === tokenId ? { ...t, revoked_at: new Date().toISOString() } : t)),
      );
    } catch (err) {
      setTokensError(err instanceof Error ? err.message : "Failed to revoke ingress token");
    } finally {
      setTokensLoading(false);
    }
  };

  if (!serviceSlug) {
    return (
      <Box>
        <Alert severity="warning">
          No Service in URL. Please navigate from a product's services list.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3, width: "100%" }}>
      <Panel title="Ingress URL" subtitle="OTLP gRPC endpoint for telemetry ingest">
        {ingressUrlError ? (
          <Typography variant="body2" color="error">
            {ingressUrlError}
          </Typography>
        ) : ingressUrl ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography variant="body2" className="om-text-mono" sx={{ fontFamily: "monospace" }}>
              {ingressUrl}
            </Typography>
            <IconButton
              size="small"
              onClick={async () => {
                try {
                  await navigator.clipboard.writeText(ingressUrl);
                } catch {
                  // ignore
                }
              }}
              aria-label="Copy ingress URL"
            >
              <ContentCopy fontSize="small" />
            </IconButton>
          </Box>
        ) : (
          <Typography variant="body2" sx={{ opacity: 0.7 }}>
            Loading…
          </Typography>
        )}
      </Panel>

      <Grid container spacing={2} columns={1}>
        <Grid size={{ xs: 12, md: 3 }}>
          <Panel title="Ingress Tokens">
            <CompGrid
              items={ingressTokens}
              loading={tokensLoading}
              error={tokensError}
              emptyMessage="No tokens yet."
              columns={tokenColumns}
              keyExtractor={(t) => t.id}
              isItemDimmed={(t) => !!t.revoked_at}
            />
          </Panel>
        </Grid>
      </Grid>
    </Box>
  );
}
